import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Cache de respostas de seção por URL.
 * Evita re-fetch quando o usuário volta a uma variante já visitada.
 * @type {Map<string, string>}
 */
const _sectionCache = new Map();

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  #onChange = this.variantChanged.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.#abortController?.abort();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    let variantId;

    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      variantId = event.target.dataset.variantId || null;
    } else if (event.target instanceof HTMLSelectElement) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      variantId = selectedOption?.dataset.variantId || null;
    }

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    // Market availability must stay consistent with the page being viewed.
    if (window.Shopify?.country) params.push(`country=${encodeURIComponent(window.Shopify.country)}`);
    if (window.Shopify?.locale) params.push(`locale=${encodeURIComponent(window.Shopify.locale)}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Processa o HTML de resposta de uma variante e atualiza o DOM.
   * Extraído para ser reutilizado pelo cache e pelo fetch.
   * @param {string} responseText
   * @param {boolean} shouldMorphMain
   */
  #applyVariantResponse(responseText, shouldMorphMain) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');
    // Defer is only useful for the initial rendering of the page. Remove it here.
    html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

    const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
    if (!textContent) throw new Error('Variant response is missing product data');
    const variant = JSON.parse(textContent);

    // Option data can still describe the previous combination during a rapid
    // colour/size change. Persist the variant resolved by the latest response.
    if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
      const url = new URL(window.location.href);
      if (variant?.id) url.searchParams.set('variant', variant.id);
      else url.searchParams.delete('variant');
      if (url.href !== window.location.href) history.replaceState({}, '', url.toString());
    }

    if (shouldMorphMain) {
      this.updateMain(html);
    } else {
      const newProduct = this.updateVariantPicker(html);

      // We grab the variant object from the response and dispatch an event with it.
      if (this.selectedOptionId) {
        this.dispatchEvent(
          new VariantUpdateEvent(variant, this.selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
          })
        );
      }
    }
  }

  /**
   * Fetches the updated section — com cache em memória.
   * Primeira seleção faz fetch normal; visitas subsequentes à mesma
   * variante são instantâneas (sem rede).
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.querySelector('[data-variant-load-error]')?.remove();
    // Cancel even when the next selection is cached: an older response must
    // never replace the variant most recently selected by the customer.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const controller = this.#abortController;
    if (_sectionCache.has(requestUrl)) {
      this.#pendingRequestUrl = undefined;
      try {
        this.#applyVariantResponse(_sectionCache.get(requestUrl), shouldMorphMain);
        return;
      } catch {
        _sectionCache.delete(requestUrl);
      }
    }

    // ── Cache miss: faz fetch e armazena o resultado ─────────────────
    // We use this to abort the previous fetch request if it's still pending.
    fetch(requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted) return;
        this.#pendingRequestUrl = undefined;
        this.#applyVariantResponse(responseText, shouldMorphMain);
        if (_sectionCache.size >= 24) _sectionCache.delete(_sectionCache.keys().next().value);
        _sectionCache.set(requestUrl, responseText);
      })
      .catch((error) => {
        if (controller.signal.aborted || !this.isConnected) return;
        this.#pendingRequestUrl = undefined;
        console.error(error);
        const notice = document.createElement('div');
        notice.dataset.variantLoadError = '';
        notice.setAttribute('role', 'alert');
        const message = document.createElement('p');
        message.textContent = Theme.translations.variant_load_error;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button-secondary';
        retry.textContent = Theme.translations.retry;
        retry.addEventListener('click', () => this.fetchUpdatedSection(requestUrl, shouldMorphMain));
        notice.append(message, retry);
        this.appendChild(notice);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
